import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  RefreshCw,
  Loader2,
  AlertCircle,
  Search,
  X,
  ExternalLink,
  Copy,
  Check,
  Image as ImageIcon,
  LayoutGrid,
  CheckCircle2,
  Wallet,
  ShoppingCart,
  TrendingUp,
} from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';

/**
 * Meta Statik Görselleri
 * ---------------------------------------------------------------
 * Tek ekranda "hangi statik görseli hangi kampanyada kullanıyoruz".
 * Ads Content (/ads-content) sayfasıyla AYNI canlı veriyi çeker
 * (POST /api/labs/meta-ads/ads-live) ama tabloyu değil, sadece
 * görselleri gösterir: görsel + altında kampanya adı.
 *
 * Kapsam dışı bırakılanlar — bunlar bizim ürettiğimiz "asset" değil:
 *   • Katalog / DPA reklamları (productSetId dolu) — görsel ürün
 *     feed'inden geliyor, kampanyaya özel bir tasarım değil.
 *   • Video reklamlar (videoIds dolu) — imageUrl'leri sadece video
 *     kapak karesi; statik görsel değil.
 *
 * Aynı görsel aynı kampanyada birden çok reklamda kullanılıyorsa TEK
 * kart olur (reklam sayısı kartta yazar). Aynı görsel farklı
 * kampanyalarda kullanılıyorsa her kampanya için ayrı kart çıkar —
 * sayfanın sorusu "bu görsel nerede kullanılıyor" olduğu için.
 */

const fmtInt = (n) => Number(n || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 });
const fmtMoney = (n) => `${Number(n || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 })} ₺`;
// Kısa para: 12.400 ₺ yerine 12,4B ₺ — 10 sütunluk ızgarada tam sayı sığmıyor.
const fmtMoneyShort = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1000000) return `${(v / 1000000).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}M ₺`;
  if (Math.abs(v) >= 1000) return `${(v / 1000).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}B ₺`;
  return `${v.toLocaleString('tr-TR', { maximumFractionDigits: 0 })} ₺`;
};
const roasOf = (revenue, spend) => (Number(spend) > 0 ? Number(revenue || 0) / Number(spend) : 0);
const fmtRoas = (r) => `${Number(r || 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}x`;
// ROAS rengi: 1x altı zarar, 3x üstü iyi. Eşikler markaya göre değişir; burada
// sadece gözün hızlı taraması için kaba bir ayrım.
const roasTone = (r) => {
  if (!r) return 'text-gray-400';
  if (r < 1) return 'text-red-600';
  if (r < 3) return 'text-amber-600';
  return 'text-emerald-600';
};

// Reklam gerçekten yayında mı? Aktif statüdeki bir reklam bütçe/hedefleme
// yüzünden hiç gösterim almıyor olabilir. Dün ya da bugün gösterim aldıysa
// fiilen yayındadır. (Ads Content sayfasındaki mantığın aynısı.)
function isDelivering(ad) {
  return Number(ad?.impressionsToday || 0) > 0 || Number(ad?.impressionsYesterday || 0) > 0;
}

// Katalog ve video reklamları eleyip geriye kalan gerçek statik görselleri döner.
function staticAssetsOf(ad) {
  if (ad?.productSetId) return [];
  if (Array.isArray(ad?.videoIds) && ad.videoIds.length) return [];
  const assets = Array.isArray(ad?.imageAssets) ? ad.imageAssets.filter((a) => a && a.url) : [];
  if (assets.length) return assets;
  // Eski cevap şekli (imageAssets alanı yokken) için geri düşüş.
  const urls = (ad?.images && ad.images.length) ? ad.images : [ad?.imageUrl, ad?.thumbnailUrl].filter(Boolean);
  return (urls || []).filter(Boolean).map((u) => ({ hash: '', url: u, width: 0, height: 0 }));
}

/**
 * Reklam listesini "görsel × reklam seti" kartlarına çevirir — en ince kırılım.
 * Kampanya kırılımı bunların üstünde toplanarak elde edilir (reklam seti
 * kırılımı kapalıyken aynı görselin farklı setlerdeki kopyaları birleşir).
 * Anahtar olarak image_hash kullanılır — CDN URL'i her çekimde değişebildiği
 * için aynı görselin tekrarını tespit eden tek kararlı kimlik odur.
 */
function buildTiles(ads) {
  const map = new Map();
  for (const ad of ads) {
    for (const asset of staticAssetsOf(ad)) {
      const assetKey = asset.hash || asset.url;
      const key = `${ad.accountId || ''}::${ad.campaignId || ''}::${ad.adsetId || ''}::${assetKey}`;
      let tile = map.get(key);
      if (!tile) {
        tile = {
          key,
          assetKey,
          url: asset.url,
          hash: asset.hash || '',
          width: asset.width || 0,
          height: asset.height || 0,
          campaignId: ad.campaignId || '',
          campaignName: ad.campaignName || '(isimsiz kampanya)',
          adsetId: ad.adsetId || '',
          adsetName: ad.adsetName || '(isimsiz reklam seti)',
          accountId: ad.accountId || '',
          accountName: ad.accountName || '',
          ads: [],
          spend30d: 0,
          impressions30d: 0,
          impressions7d: 0,
          clicks30d: 0,
          purchases30d: 0,
          revenue30d: 0,
          spend7d: 0,
          purchases7d: 0,
          revenue7d: 0,
          delivering: false,
          createdTime: '',
        };
        map.set(key, tile);
      }
      tile.ads.push(ad);
      tile.spend30d += Number(ad.spend30d || 0);
      tile.impressions30d += Number(ad.impressions30d || 0);
      tile.impressions7d += Number(ad.impressions7d || 0);
      tile.clicks30d += Number(ad.clicks30d || 0);
      tile.purchases30d += Number(ad.purchases30d || 0);
      tile.revenue30d += Number(ad.revenue30d || 0);
      tile.spend7d += Number(ad.spend7d || 0);
      tile.purchases7d += Number(ad.purchases7d || 0);
      tile.revenue7d += Number(ad.revenue7d || 0);
      tile.delivering = tile.delivering || isDelivering(ad);
      if (ad.createdTime && ad.createdTime > tile.createdTime) tile.createdTime = ad.createdTime;
      // Boyut bilgisi hangi reklamdan gelirse gelsin aynı — ilk dolu olanı tut.
      if (!tile.width && asset.width) { tile.width = asset.width; tile.height = asset.height; }
    }
  }
  return Array.from(map.values());
}

/** Aynı görselin farklı reklam setlerindeki kopyalarını tek kartta birleştirir. */
function mergeTilesByAsset(tiles) {
  const map = new Map();
  for (const t of tiles) {
    const cur = map.get(t.assetKey);
    if (!cur) {
      map.set(t.assetKey, { ...t, key: `${t.campaignId}::${t.assetKey}`, adsetId: '', adsetName: '', ads: [...t.ads] });
      continue;
    }
    cur.ads.push(...t.ads);
    cur.spend30d += t.spend30d;
    cur.impressions30d += t.impressions30d;
    cur.impressions7d += t.impressions7d;
    cur.clicks30d += t.clicks30d;
    cur.purchases30d += t.purchases30d;
    cur.revenue30d += t.revenue30d;
    cur.spend7d += t.spend7d;
    cur.purchases7d += t.purchases7d;
    cur.revenue7d += t.revenue7d;
    cur.delivering = cur.delivering || t.delivering;
    if (t.createdTime > cur.createdTime) cur.createdTime = t.createdTime;
    if (!cur.width && t.width) { cur.width = t.width; cur.height = t.height; }
  }
  return Array.from(map.values());
}

/**
 * Görsel kartlarını kampanya kutularına toplar — ekranın ana birimi kampanya.
 * `splitByAdset` açıkken kutunun içi reklam seti başlıklarına bölünür; kapalıyken
 * aynı görselin farklı setlerdeki kopyaları tek karede birleşir.
 */
function groupByCampaign(tiles, splitByAdset) {
  const map = new Map();
  for (const t of tiles) {
    const key = `${t.accountId}::${t.campaignId}`;
    let g = map.get(key);
    if (!g) {
      g = {
        key,
        campaignId: t.campaignId,
        campaignName: t.campaignName,
        accountId: t.accountId,
        accountName: t.accountName,
        assets: [],
        sections: [],
        spend30d: 0,
        impressions7d: 0,
        impressions30d: 0,
        clicks30d: 0,
        purchases30d: 0,
        revenue30d: 0,
        delivering: false,
        createdTime: '',
      };
      map.set(key, g);
    }
    g.assets.push(t);
    g.delivering = g.delivering || t.delivering;
    if (t.createdTime > g.createdTime) g.createdTime = t.createdTime;
  }
  // Kampanya toplamları görsellerin toplamı DEĞİL, kampanyadaki benzersiz
  // reklamların toplamı: aynı reklam birden çok görsel taşıyorsa (karusel,
  // dinamik creative) harcaması her görselde tekrar sayılmamalı.
  for (const g of map.values()) {
    const seen = new Set();
    for (const t of g.assets) {
      for (const ad of t.ads) {
        if (seen.has(ad.adId)) continue;
        seen.add(ad.adId);
        g.spend30d += Number(ad.spend30d || 0);
        g.impressions7d += Number(ad.impressions7d || 0);
        g.impressions30d += Number(ad.impressions30d || 0);
        g.clicks30d += Number(ad.clicks30d || 0);
        g.purchases30d += Number(ad.purchases30d || 0);
        g.revenue30d += Number(ad.revenue30d || 0);
      }
    }
    g.adCount = seen.size;
    g.roas30d = roasOf(g.revenue30d, g.spend30d);

    if (splitByAdset) {
      const secs = new Map();
      for (const t of g.assets) {
        let s = secs.get(t.adsetId);
        if (!s) {
          s = {
            key: `${g.key}::${t.adsetId}`,
            adsetId: t.adsetId,
            adsetName: t.adsetName,
            assets: [],
            spend30d: 0,
            impressions7d: 0,
            purchases30d: 0,
            revenue30d: 0,
            delivering: false,
            adIds: new Set(),
          };
          secs.set(t.adsetId, s);
        }
        s.assets.push(t);
        s.delivering = s.delivering || t.delivering;
        for (const ad of t.ads) {
          if (s.adIds.has(ad.adId)) continue;
          s.adIds.add(ad.adId);
          s.spend30d += Number(ad.spend30d || 0);
          s.impressions7d += Number(ad.impressions7d || 0);
          s.purchases30d += Number(ad.purchases30d || 0);
          s.revenue30d += Number(ad.revenue30d || 0);
        }
      }
      g.sections = Array.from(secs.values())
        .map((s) => ({ ...s, adCount: s.adIds.size, roas30d: roasOf(s.revenue30d, s.spend30d) }))
        .sort((a, b) => b.spend30d - a.spend30d);
      g.sections.forEach((s) => s.assets.sort((a, b) => b.spend30d - a.spend30d));
      g.assets.sort((a, b) => b.spend30d - a.spend30d);
    } else {
      // Reklam seti kırılımı kapalı: aynı görsel kampanyada tek kare.
      g.assets = mergeTilesByAsset(g.assets).sort((a, b) => b.spend30d - a.spend30d);
      g.sections = [{ key: `${g.key}::all`, adsetId: '', adsetName: '', assets: g.assets }];
    }
  }
  return Array.from(map.values());
}

const SORTS = {
  spend: { label: 'Harcama (30g)', cmp: (a, b) => b.spend30d - a.spend30d },
  purchases: { label: 'Satın alma (30g)', cmp: (a, b) => b.purchases30d - a.purchases30d },
  revenue: { label: 'Gelir (30g)', cmp: (a, b) => b.revenue30d - a.revenue30d },
  roas: { label: 'ROAS (30g)', cmp: (a, b) => b.roas30d - a.roas30d },
  impressions: { label: 'Gösterim (7g)', cmp: (a, b) => b.impressions7d - a.impressions7d },
  assets: { label: 'Görsel sayısı', cmp: (a, b) => b.assets.length - a.assets.length },
  newest: { label: 'En yeni', cmp: (a, b) => (b.createdTime || '').localeCompare(a.createdTime || '') },
  campaign: { label: 'Kampanya adı', cmp: (a, b) => a.campaignName.localeCompare(b.campaignName, 'tr') },
};

function StatCard({ icon, label, value, sub }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 px-4 py-3 flex items-center gap-3 shadow-sm">
      <div className="w-10 h-10 shrink-0 rounded-xl bg-gray-100 text-gray-600 flex items-center justify-center">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 truncate">{label}</p>
        <p className="text-xl font-semibold tabular-nums text-gray-900">{value}</p>
        {sub && <p className="text-xs text-gray-500 mt-0.5 truncate" title={sub}>{sub}</p>}
      </div>
    </div>
  );
}

// Kampanya kutusunun içindeki tek görsel. Kampanya adı kutunun başlığında
// yazdığı için burada tekrar etmiyor — sadece görselin kendisi ve durumu.
function AssetThumb({ tile, onClick }) {
  const [broken, setBroken] = useState(false);
  const roas = roasOf(tile.revenue30d, tile.spend30d);
  const cpa = tile.purchases30d > 0 ? tile.spend30d / tile.purchases30d : 0;
  const title = [
    tile.ads.length > 1 ? `${tile.ads.length} reklamda kullanılıyor` : (tile.ads[0]?.adName || ''),
    tile.width > 0 ? `${tile.width}×${tile.height} px` : '',
    `Son 30 gün: ${fmtMoney(tile.spend30d)} harcama`,
    `${fmtInt(tile.purchases30d)} satın alma`,
    `${fmtMoney(tile.revenue30d)} gelir`,
    tile.spend30d > 0 ? `ROAS ${fmtRoas(roas)}` : '',
    cpa > 0 ? `CPA ${fmtMoney(cpa)}` : '',
    `${fmtInt(tile.clicks30d)} tıklama`,
    tile.delivering ? 'Yayında' : 'Yayında değil',
  ].filter(Boolean).join('\n');
  return (
    <button
      type="button"
      onClick={() => onClick(tile)}
      title={title}
      className="group text-left focus:outline-none"
    >
      <div className="relative aspect-square rounded-xl overflow-hidden bg-gray-50 ring-1 ring-gray-200 group-hover:ring-gray-900 group-focus:ring-2 group-focus:ring-gray-900 transition-all">
        {broken ? (
          <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-gray-300">
            <ImageIcon className="w-6 h-6" />
            <span className="text-[9px]">yüklenemedi</span>
          </div>
        ) : (
          <img
            src={tile.url}
            alt=""
            loading="lazy"
            onError={() => setBroken(true)}
            className="w-full h-full object-contain group-hover:scale-[1.04] transition-transform duration-200"
          />
        )}
        {tile.delivering && (
          <span
            className="absolute top-1.5 left-1.5 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-white"
            title="Dün veya bugün gösterim aldı"
          />
        )}
        {tile.ads.length > 1 && (
          <span className="absolute bottom-1.5 right-1.5 text-[9px] font-semibold px-1.5 py-0.5 rounded-lg bg-gray-900/80 text-white">
            ×{tile.ads.length}
          </span>
        )}
      </div>
      {/* Görselin performansı — sayfanın asıl sorusu "hangi görsel satıyor". */}
      <div className="mt-1.5 leading-tight">
        <div className="flex items-baseline justify-between gap-1">
          <span className={`text-[11px] font-bold tabular-nums ${roasTone(roas)}`}>
            {tile.spend30d > 0 ? fmtRoas(roas) : '—'}
          </span>
          <span className="text-[10px] tabular-nums text-gray-700 font-medium">
            {fmtInt(tile.purchases30d)}<span className="text-gray-400 font-normal"> sat.</span>
          </span>
        </div>
        <div className="text-[9px] tabular-nums text-gray-400">
          {fmtMoneyShort(tile.spend30d)} → {fmtMoneyShort(tile.revenue30d)}
        </div>
      </div>
    </button>
  );
}

const THUMB_GRID = 'grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-3';

// Bir kampanya = bir kutu. Basligi kampanya adi, icinde o kampanyada kullanilan
// tum statik gorseller — reklam seti kirilimi acikken set set ayrilmis halde.
function CampaignBox({ group, onAssetClick, splitByAdset }) {
  return (
    <section className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-gray-100 bg-gray-50/70">
        <div className="min-w-0 flex items-center gap-2">
          {group.delivering && (
            <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" title="Kampanyada yayında olan reklam var" />
          )}
          <h3 className="text-sm font-semibold text-gray-900 break-words" title={group.campaignName}>
            {group.campaignName}
          </h3>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-gray-500 shrink-0">
          {splitByAdset && <span className="tabular-nums">{fmtInt(group.sections.length)} set</span>}
          <span className="tabular-nums">{fmtInt(group.assets.length)} görsel</span>
          <span className="tabular-nums" title="Son 30 gün harcama">{fmtMoney(group.spend30d)}</span>
          <span className="tabular-nums" title="Son 30 gün satın alma">{fmtInt(group.purchases30d)} satın alma</span>
          <span className="tabular-nums" title="Son 30 gün gelir">{fmtMoney(group.revenue30d)}</span>
          <span className={`tabular-nums font-semibold ${roasTone(group.roas30d)}`} title="Son 30 gün ROAS">
            {group.spend30d > 0 ? fmtRoas(group.roas30d) : '—'}
          </span>
        </div>
      </div>

      {splitByAdset ? (
        <div className="divide-y divide-gray-100">
          {group.sections.map((s) => (
            <div key={s.key} className="px-4 py-3">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2.5">
                <div className="min-w-0 flex items-center gap-2">
                  <span
                    className={`w-1.5 h-1.5 rounded-full shrink-0 ${s.delivering ? 'bg-emerald-500' : 'bg-gray-300'}`}
                    title={s.delivering ? 'Sette yayında reklam var' : 'Sette yayında reklam yok'}
                  />
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 shrink-0">Reklam seti</span>
                  <span className="text-xs font-medium text-gray-800 break-words" title={s.adsetName}>{s.adsetName}</span>
                </div>
                <div className="flex items-center gap-3 text-[10px] text-gray-500 shrink-0">
                  <span className="tabular-nums">{fmtInt(s.assets.length)} görsel</span>
                  <span className="tabular-nums">{fmtInt(s.adCount)} reklam</span>
                  <span className="tabular-nums" title="Son 30 gün harcama">{fmtMoney(s.spend30d)}</span>
                  <span className="tabular-nums" title="Son 30 gün satın alma">{fmtInt(s.purchases30d)} satın alma</span>
                  <span className={`tabular-nums font-semibold ${roasTone(s.roas30d)}`} title="Son 30 gün ROAS">
                    {s.spend30d > 0 ? fmtRoas(s.roas30d) : '—'}
                  </span>
                </div>
              </div>
              <div className={THUMB_GRID}>
                {s.assets.map((t) => (
                  <AssetThumb key={t.key} tile={t} onClick={onAssetClick} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={`p-4 ${THUMB_GRID}`}>
          {group.assets.map((t) => (
            <AssetThumb key={t.key} tile={t} onClick={onAssetClick} />
          ))}
        </div>
      )}
    </section>
  );
}

function Metric({ label, value, sub, cls = 'text-gray-900' }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{label}</div>
      <div className={`text-sm tabular-nums font-medium ${cls}`}>{value}</div>
      {sub && <div className="text-[10px] text-gray-400 tabular-nums">{sub}</div>}
    </div>
  );
}

function AssetModal({ tile, onClose }) {
  const [copied, setCopied] = useState(false);
  const roas30 = roasOf(tile.revenue30d, tile.spend30d);
  const roas7 = roasOf(tile.revenue7d, tile.spend7d);
  const cpa30 = tile.purchases30d > 0 ? tile.spend30d / tile.purchases30d : 0;
  const ctr30 = tile.impressions30d > 0 ? (tile.clicks30d / tile.impressions30d) * 100 : 0;
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(tile.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) { /* pano izni yoksa sessiz geç */ }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl my-8 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-gray-100 bg-gray-50">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-gray-800 break-words" title={tile.campaignName}>
              {tile.campaignName}
            </h3>
            {tile.adsetName && (
              <p className="text-xs text-gray-600 mt-0.5">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mr-1.5">Reklam seti</span>
                {tile.adsetName}
              </p>
            )}
            <p className="text-xs text-gray-500 mt-0.5">
              {tile.accountName || tile.accountId} · {tile.ads.length} reklam
              {tile.width > 0 ? ` · ${tile.width}×${tile.height} px` : ''}
            </p>
          </div>
          <button onClick={onClose} className="shrink-0 p-1.5 rounded-xl hover:bg-gray-200 text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5 max-h-[75vh] overflow-y-auto">
          <div className="bg-gray-50 rounded-xl ring-1 ring-gray-200 p-3 flex items-center justify-center">
            <img src={tile.url} alt={tile.campaignName} className="max-h-[45vh] w-auto object-contain rounded-lg" />
          </div>

          <div className="flex flex-wrap gap-2">
            <a
              href={tile.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs font-medium px-3 py-2 rounded-xl bg-gray-900 text-white hover:bg-gray-800"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Orijinali aç
            </a>
            <button
              onClick={copyUrl}
              className="inline-flex items-center gap-2 text-xs font-medium px-3 py-2 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? 'Kopyalandı' : 'Görsel URL kopyala'}
            </button>
          </div>

          {/* Performans — son 30 gün */}
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2">
              Performans · son 30 gün
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <Metric label="Harcama" value={fmtMoney(tile.spend30d)} />
              <Metric label="Satın alma" value={fmtInt(tile.purchases30d)} />
              <Metric label="Gelir" value={fmtMoney(tile.revenue30d)} />
              <Metric
                label="ROAS"
                value={tile.spend30d > 0 ? fmtRoas(roas30) : '—'}
                cls={roasTone(roas30)}
              />
              <Metric label="CPA" value={cpa30 > 0 ? fmtMoney(cpa30) : '—'} />
              <Metric label="Tıklama" value={fmtInt(tile.clicks30d)} sub={ctr30 > 0 ? `CTR %${ctr30.toFixed(2)}` : ''} />
              <Metric label="Gösterim" value={fmtInt(tile.impressions30d)} sub={`7g: ${fmtInt(tile.impressions7d)}`} />
              <Metric
                label="Durum"
                value={tile.delivering ? 'Yayında' : 'Yayında değil'}
                cls={tile.delivering ? 'text-emerald-700' : 'text-gray-500'}
              />
            </div>
          </div>

          {/* Son 7 gün — trend mi kırılıyor, ivme mi var */}
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2">
              Performans · son 7 gün
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <Metric label="Harcama" value={fmtMoney(tile.spend7d)} />
              <Metric label="Satın alma" value={fmtInt(tile.purchases7d)} />
              <Metric label="Gelir" value={fmtMoney(tile.revenue7d)} />
              <Metric
                label="ROAS"
                value={tile.spend7d > 0 ? fmtRoas(roas7) : '—'}
                cls={roasTone(roas7)}
              />
            </div>
          </div>

          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2">
              Bu görseli kullanan reklamlar ({tile.ads.length})
            </div>
            <div className="rounded-xl ring-1 ring-gray-200 divide-y divide-gray-100 max-h-64 overflow-y-auto">
              {tile.ads.map((a, i) => (
                <div key={`${a.adId}-${i}`} className="px-3 py-2 text-xs">
                  <div className="font-medium text-gray-800 break-words">{a.adName || '(isimsiz reklam)'}</div>
                  <div className="text-[11px] text-gray-500 break-words">
                    {a.adsetName || '—'} · {a.effectiveStatus || a.status || '—'}
                  </div>
                  <div className="text-[11px] text-gray-600 tabular-nums mt-0.5">
                    {fmtMoney(a.spend30d)} · {fmtInt(a.purchases30d)} satın alma · {fmtMoney(a.revenue30d)}
                    <span className={`ml-1.5 font-semibold ${roasTone(roasOf(a.revenue30d, a.spend30d))}`}>
                      {Number(a.spend30d) > 0 ? fmtRoas(roasOf(a.revenue30d, a.spend30d)) : '—'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MetaStaticAssets() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id || localStorage.getItem('currentBrandId') || '';

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [deliveringOnly, setDeliveringOnly] = useState(true);
  // Kampanya kutusunun içini reklam setlerine böl. Kapalıyken aynı görselin
  // farklı setlerdeki kopyaları tek karede birleşir.
  const [splitByAdset, setSplitByAdset] = useState(true);
  const [sortKey, setSortKey] = useState('spend');
  const [account, setAccount] = useState('all');
  const [selected, setSelected] = useState(null);
  const inFlight = useRef(false);

  const fetchAds = useCallback(async () => {
    if (!brandId) { setError('Marka seçilmedi'); return; }
    setLoading(true);
    setError('');
    try {
      const resp = await fetch(`${API_BASE}/api/labs/meta-ads/ads-live`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify({ brandId }),
      });
      const json = await resp.json();
      if (!resp.ok) throw new Error(json?.error || `HTTP ${resp.status}`);
      setData(json);
    } catch (e) {
      setError(e?.message || 'Meta verisi çekilemedi');
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => {
    if (!brandId || inFlight.current) return;
    inFlight.current = true;
    fetchAds().finally(() => { inFlight.current = false; });
  }, [brandId, fetchAds]);

  const allAds = useMemo(() => data?.ads || [], [data]);

  // Kapsam dışı bırakılanların sayısı — "neden bu kadar az görsel var?"
  // sorusunun cevabı ekranda dursun.
  const excluded = useMemo(() => {
    let catalog = 0;
    let video = 0;
    for (const a of allAds) {
      if (a.productSetId) catalog += 1;
      else if (Array.isArray(a.videoIds) && a.videoIds.length) video += 1;
    }
    return { catalog, video };
  }, [allAds]);

  const groups = useMemo(() => {
    let ads = allAds;
    if (deliveringOnly) ads = ads.filter(isDelivering);
    if (account !== 'all') ads = ads.filter((a) => String(a.accountId || '') === account);
    let list = buildTiles(ads);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((t) =>
        t.campaignName.toLowerCase().includes(q)
        || t.ads.some((a) => (a.adName || '').toLowerCase().includes(q) || (a.adsetName || '').toLowerCase().includes(q)));
    }
    return groupByCampaign(list, splitByAdset).sort(SORTS[sortKey]?.cmp || SORTS.spend.cmp);
  }, [allAds, deliveringOnly, account, search, sortKey, splitByAdset]);

  const adsetCount = useMemo(
    () => (splitByAdset ? groups.reduce((s, g) => s + g.sections.length, 0) : 0),
    [groups, splitByAdset],
  );

  const tileCount = useMemo(() => groups.reduce((s, g) => s + g.assets.length, 0), [groups]);
  const uniqueAssetCount = useMemo(() => {
    const seen = new Set();
    groups.forEach((g) => g.assets.forEach((t) => seen.add(t.hash || t.url)));
    return seen.size;
  }, [groups]);
  const totalSpend = useMemo(() => groups.reduce((s, g) => s + g.spend30d, 0), [groups]);
  const totalPurchases = useMemo(() => groups.reduce((s, g) => s + g.purchases30d, 0), [groups]);
  const totalRevenue = useMemo(() => groups.reduce((s, g) => s + g.revenue30d, 0), [groups]);
  const totalRoas = roasOf(totalRevenue, totalSpend);
  const accounts = data?.accounts || [];

  return (
    <div className="p-6 space-y-6 bg-[#f6f8ff] min-h-screen">
      {/* Başlık */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <img src="/6033716.png" alt="Meta" className="w-6 h-6 rounded-sm" />
            <h1 className="text-2xl font-semibold text-gray-800">Meta Statik Görselleri</h1>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Her kampanya bir kutu, kutunun içinde o kampanyada kullandığımız statik görseller.
            Katalog (ürün feed'i) ve video reklamlar listelenmez.
          </p>
        </div>
        <button
          onClick={() => !loading && fetchAds()}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl bg-white border border-gray-200 text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Yenile
        </button>
      </div>

      {/* Özet */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
        <StatCard
          icon={<LayoutGrid className="w-5 h-5" />}
          label="Kampanya"
          value={fmtInt(groups.length)}
          sub={splitByAdset ? `${fmtInt(adsetCount)} reklam seti` : 'her biri bir kutu'}
        />
        <StatCard icon={<ImageIcon className="w-5 h-5" />} label="Görsel" value={fmtInt(tileCount)} sub={`${fmtInt(uniqueAssetCount)} benzersiz`} />
        <StatCard icon={<Wallet className="w-5 h-5" />} label="Harcama (30g)" value={fmtMoney(totalSpend)} sub="listelenen reklamlar" />
        <StatCard icon={<ShoppingCart className="w-5 h-5" />} label="Satın alma (30g)" value={fmtInt(totalPurchases)} sub={totalPurchases > 0 ? `CPA ${fmtMoney(totalSpend / totalPurchases)}` : ''} />
        <StatCard icon={<TrendingUp className="w-5 h-5" />} label="ROAS (30g)" value={totalSpend > 0 ? fmtRoas(totalRoas) : '—'} sub={`${fmtMoney(totalRevenue)} gelir`} />
        <StatCard
          icon={<AlertCircle className="w-5 h-5" />}
          label="Kapsam dışı reklam"
          value={fmtInt(excluded.catalog + excluded.video)}
          sub={`${fmtInt(excluded.catalog)} katalog · ${fmtInt(excluded.video)} video`}
        />
      </div>

      {/* Hesap durumu */}
      {accounts.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {accounts.map((a, i) => (
            <div
              key={`${a.accountId || 'acc'}-${i}`}
              className={`inline-flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-xl shadow-sm ${a.error ? 'bg-red-50 text-red-700 ring-1 ring-red-200' : 'bg-white text-gray-700 border border-gray-200'}`}
              title={a.error || ''}
            >
              {a.error ? <AlertCircle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3 text-emerald-600" />}
              {a.accountName || a.accountId || 'Hesap'} · {a.count ?? 0} reklam
              {a.error && <span className="italic">— {String(a.error).slice(0, 60)}</span>}
            </div>
          ))}
        </div>
      )}

      {/* Filtreler */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Kampanya, reklam seti veya reklam adı ara…"
            className="w-full pl-9 pr-8 py-2 text-sm rounded-xl border border-gray-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <label
          className="inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none px-3 py-2 rounded-xl border border-gray-200 bg-white shadow-sm hover:bg-gray-50"
          title="Dün veya bugün gösterim almış reklamlar. Kapatınca aktif kampanyalardaki durdurulmuş reklamların görselleri de listelenir."
        >
          <input type="checkbox" checked={deliveringOnly} onChange={(e) => setDeliveringOnly(e.target.checked)} className="accent-gray-900" />
          Sadece yayındakiler
        </label>

        <label
          className="inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none px-3 py-2 rounded-xl border border-gray-200 bg-white shadow-sm hover:bg-gray-50"
          title="Kampanya kutusunun içini reklam setlerine böler. Kapatınca aynı görselin farklı setlerdeki kopyaları tek karede birleşir."
        >
          <input type="checkbox" checked={splitByAdset} onChange={(e) => setSplitByAdset(e.target.checked)} className="accent-gray-900" />
          Reklam setine göre ayır
        </label>

        {accounts.length > 1 && (
          <select
            value={account}
            onChange={(e) => setAccount(e.target.value)}
            className="text-sm px-3 py-2 rounded-xl border border-gray-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
          >
            <option value="all">Tüm hesaplar</option>
            {accounts.map((a, i) => (
              <option key={`${a.accountId}-${i}`} value={String(a.accountId || '')}>
                {a.accountName || a.accountId}
              </option>
            ))}
          </select>
        )}

        <select
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value)}
          className="text-sm px-3 py-2 rounded-xl border border-gray-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
        >
          {Object.entries(SORTS).map(([k, v]) => (
            <option key={k} value={k}>Sırala: {v.label}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-2xl px-5 py-4 text-sm text-red-700 flex items-start gap-3">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="flex-1">{error}</div>
          <button onClick={fetchAds} disabled={loading} className="text-xs font-medium text-red-700 underline disabled:opacity-50">
            Tekrar dene
          </button>
        </div>
      )}

      {/* Kampanya kutuları */}
      {loading && !groups.length ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/70">
                <div className="h-3.5 w-64 bg-gray-100 rounded animate-pulse" />
              </div>
              <div className="p-4 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-3">
                {Array.from({ length: 10 }).map((__, j) => (
                  <div key={j} className="aspect-square bg-gray-100 rounded-xl animate-pulse" />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : groups.length ? (
        <>
          <div className="space-y-4">
            {groups.map((g) => (
              <CampaignBox key={g.key} group={g} onAssetClick={setSelected} splitByAdset={splitByAdset} />
            ))}
          </div>
          <div className="text-[11px] text-gray-400 space-y-1">
            <p>
              {fmtInt(groups.length)} kampanya{splitByAdset ? ` · ${fmtInt(adsetCount)} reklam seti` : ''} · {fmtInt(tileCount)} görsel
              ({fmtInt(uniqueAssetCount)} benzersiz). Aynı görsel birden fazla kampanyada/sette kullanılıyorsa her birinin
              altında ayrı ayrı görünür. Görsele tıkla → büyük hâli, 30/7 günlük performans ve o görseli kullanan reklamlar.
            </p>
            <p>
              Metrikler son 30 gün, Meta'nın varsayılan atıf penceresiyle (7 gün tıklama / 1 gün görüntüleme) —
              Ads Manager ile aynı. Satın alma ve gelir tek kanonik action tipinden alınır (örtüşen tipler toplanmaz).
              Bir reklam birden çok görsel taşıyorsa (karusel, dinamik creative) o reklamın performansı görsel başına
              ayrıştırılamaz; her karede reklamın tamamı görünür — kampanya ve set toplamları ise reklamı tek sayar.
            </p>
          </div>
        </>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 py-16 text-center text-sm text-gray-500">
          {loading ? 'Yükleniyor…' : 'Statik görsel bulunamadı. (Filtreleri gevşetmeyi deneyebilirsin.)'}
        </div>
      )}

      {selected && <AssetModal tile={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
